import { mutate } from 'swr';
import { useMemo, useCallback } from 'react';

import axiosInstance from 'src/utils/axios';
import {
  BASE_API_PATH,
  authenticatedSwrOptions,
  unAuthenticatedSwrOptions,
} from 'src/utils/constants';

import { CONFIG } from 'src/config-global';

import useAuthenticatedSWR from 'src/auth/hooks/use-authenticated-swr';
import useUnauthenticatedSWR from 'src/auth/hooks/use-unauthenticated-swr';

import { zerreactorBackendEndpoints } from './zerreactorBackendEndpoints';

// TEMP
//----------------------------------------------------------

// Organization
export function useGetOrganizationById(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? zerreactorBackendEndpoints.Organization.Get(id) : null,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      organization: data || {},
      organizationLoading: isLoading,
      organizationError: error,
      organizationValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Unapproved Organizations
export function useGetTempUnapprovedOrganizations(countryId, hasSpecialPermission, userCountryId) {
  const effectiveUrl =
    hasSpecialPermission && !countryId
      ? `${BASE_API_PATH}/Temp/GetUnapprovedOrganizations`
      : zerreactorBackendEndpoints.Temp.GetUnapprovedOrganizations(
          hasSpecialPermission ? countryId : userCountryId
        );

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    effectiveUrl,
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      unapprovedOrganizations: data || [],
      unapprovedOrganizationsLoading: isLoading,
      unapprovedOrganizationsError: error,
      unapprovedOrganizationsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Organizations
export function useGetTempOrganizations(countryId, hasSpecialPermission, userCountryId, isSignUp) {
  const effectiveUrl = isSignUp
    ? zerreactorBackendEndpoints.Temp.GetOrganizations(countryId)
    : hasSpecialPermission && !countryId
      ? `${BASE_API_PATH}/Temp/GetOrganizations`
      : zerreactorBackendEndpoints.Temp.GetOrganizations(
          hasSpecialPermission ? countryId : userCountryId
        );

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    effectiveUrl,
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      organizations: data || [],
      organizationsLoading: isLoading,
      organizationsError: error,
      organizationsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Unapproved Users
export function useGetTempUnapprovedUsers(countryId, hasSpecialPermission, userCountryId) {
  const effectiveUrl =
    hasSpecialPermission && !countryId
      ? `${BASE_API_PATH}/Temp/GetUnapprovedUsers`
      : zerreactorBackendEndpoints.Temp.GetUnapprovedUsers(
          hasSpecialPermission ? countryId : userCountryId
        );

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    effectiveUrl,
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      unapprovedUsers: data || [],
      unapprovedUsersLoading: isLoading,
      unapprovedUsersError: error,
      unapprovedUsersValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Users with No Organization
export function useGetUsersWithNoOrganization(countryId, hasSpecialPermission, userCountryId) {
  const effectiveUrl =
    hasSpecialPermission && !countryId
      ? `${BASE_API_PATH}/Temp/GetUsersOrganizationNotExist`
      : zerreactorBackendEndpoints.Temp.GetUsersOrganizationNotExist(
          hasSpecialPermission ? countryId : userCountryId
        );

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    effectiveUrl,
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      usersWithNoOrganization: data || [],
      usersWithNoOrganizationLoading: isLoading,
      usersWithNoOrganizationError: error,
      usersWithNoOrganizationValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Venue
export function useGetAllVenues() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Venue.GetAll,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      venues: data || [],
      venuesLoading: isLoading,
      venuesError: error,
      venuesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

// Mutation Hooks

export function useSignUpUser() {
  const signUpUser = async (userData) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Auth.signUpUser,
        userData
      );
      return response.data;
    } catch (error) {
      console.error('Error signing up user:', error);
      throw error;
    }
  };

  return { signUpUser };
}

export function useSendResetPasswordEmail() {
  const sendResetPasswordEmail = async (email) => {
    try {
      const requestData = {
        Email: email,
      };

      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Auth.sendResetPasswordEmail,
        requestData
      );

      // If response.data is null but responseText exists, parse it
      if (response.data === null && response.responseText) {
        const parsedResponse = JSON.parse(response.responseText);
        return parsedResponse;
      }

      // If response.data is null but request.responseText exists, parse it
      if (response.data === null && response.request?.responseText) {
        const parsedResponse = JSON.parse(response.request.responseText);
        return parsedResponse;
      }

      return response.data;
    } catch (error) {
      console.error('Error sending reset password email:', error);
      throw error;
    }
  };

  return { sendResetPasswordEmail };
}

export function useForgetPassword() {
  const forgetPassword = async (token) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Auth.forgetPassword, {
        token,
      });

      // If response.data is null but request.responseText exists, parse it
      if (response.data === null && response.request?.responseText) {
        const parsedResponse = JSON.parse(response.request.responseText);
        return parsedResponse;
      }

      return response.data;
    } catch (error) {
      console.error('Error forgetting password:', error);
      throw error;
    }
  };

  return { forgetPassword };
}

export function useResetUserPassword() {
  const resetUserPassword = async (newPassword, token) => {
    try {
      const requestData = {
        Password: newPassword,
      };

      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Auth.resetPassword,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // If response.data is null but request.responseText exists, parse it
      if (response.data === null && response.request?.responseText) {
        const parsedResponse = JSON.parse(response.request.responseText);
        return parsedResponse;
      }

      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  return { resetUserPassword };
}

export function useTempApproveOrganizations() {
  const approveOrganizations = async (updateUserId, organizationIds) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Temp.ApproveOrganizations(updateUserId),
        organizationIds
      );
      return response.data;
    } catch (error) {
      console.error('Error approving organizations:', error);
      throw error;
    }
  };

  return { approveOrganizations };
}

export function useTempApproveUsers() {
  const approveUsers = async (updateUserId, userIds) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Temp.ApproveUsers(updateUserId),
        userIds
      );
      return response.data;
    } catch (error) {
      console.error('Error approving users:', error);
      throw error;
    }
  };

  return { approveUsers };
}

export function useSetOrganizationToUser() {
  const setOrganizationToUser = async (updateUserId, userId, organizationId) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Temp.SetOrganizationToUser(updateUserId),
        { userId, organizationId }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting organization to user:', error);
      throw error;
    }
  };

  return { setOrganizationToUser };
}

// SYSTEM
//----------------------------------------------------------

// Group Role
export function useGetGroupRole(groupId, roleId) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    groupId && roleId ? zerreactorBackendEndpoints.System.GetGroupRole(groupId, roleId) : null,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      groupRole: data || {},
      groupRoleLoading: isLoading,
      groupRoleError: error,
      groupRoleValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Fair

export function useGetFairsUnauthorized() {
  const effectiveUrl = zerreactorBackendEndpoints.Temp.GetFairs;

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    effectiveUrl,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      affiliatedFairs: data || [],
      affiliatedFairsLoading: isLoading,
      affiliatedFairsError: error,
      affiliatedFairsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAllFairs() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Fair.GetAll,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      fairs: data || [],
      fairsLoading: isLoading,
      fairsError: error,
      fairsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetFairById(id) {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Fair.GetById(id),
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      fair: data || {},
      fairLoading: isLoading,
      fairError: error,
      fairValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useFairOperations() {
  const createFair = async (fairData) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Fair.Create, fairData);

      return response.data;
    } catch (error) {
      console.error('Error creating fair:', error);
      throw error;
    }
  };

  const updateFair = async (id, fairData) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Fair.Update(id),
        fairData
      );

      return response.data;
    } catch (error) {
      console.error('Error updating fair:', error);
      throw error;
    }
  };

  const deleteFair = async (id) => {
    try {
      const response = await axiosInstance.delete(zerreactorBackendEndpoints.Fair.Delete(id));

      return response.data;
    } catch (error) {
      console.error('Error deleting fair:', error);
      throw error;
    }
  };

  return { createFair, updateFair, deleteFair };
}

// Categories

export function useGetAllTempCategories() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Temp.GetCategories,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      tempCategories: data || [],
      tempCategoriesLoading: isLoading,
      tempCategoriesError: error,
      tempCategoriesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

// Jury

export function useGetAllUnapprovedJuries() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Jury.GetAllUnapproved,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      unapprovedJuries: data || [],
      unapprovedJuriesLoading: isLoading,
      unapprovedJuriesError: error,
      unapprovedJuriesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAllApprovedJuries() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Jury.GetAllApproved,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      approvedJuries: data || [],
      approvedJuriesLoading: isLoading,
      approvedJuriesError: error,
      approvedJuriesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAllJuries() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Jury.GetAll,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      juries: data || [],
      juriesLoading: isLoading,
      juriesError: error,
      juriesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetJuryById(id) {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Jury.GetById(id),
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      jury: data || {},
      juryLoading: isLoading,
      juryError: error,
      juryValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useJuryOperations() {
  const approveJury = async (id) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Jury.Approve(id));

      return response.data;
    } catch (error) {
      console.error('Error approving jury:', error);
      throw error;
    }
  };

  const rejectJury = async (id) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Jury.Reject(id));

      return response.data;
    } catch (error) {
      console.error('Error approving jury:', error);
      throw error;
    }
  };

  const deleteJuryRegistration = async (id) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Jury.DeleteJuryRegistration(id));

      return response.data;
    } catch (error) {
      console.error('Error approving jury:', error);
      throw error;
    }
  };

  const createJury = async (juryData) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Jury.Create, juryData);

      return response.data;
    } catch (error) {
      console.error('Error creating jury:', error);
      throw error;
    }
  };

  const updateJury = async (id, juryData) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Jury.Update(id),
        juryData
      );

      return response.data;
    } catch (error) {
      console.error('Error updating jury:', error);
      throw error;
    }
  };

  const deleteJury = async (id) => {
    try {
      const response = await axiosInstance.delete(zerreactorBackendEndpoints.Jury.Delete(id));

      return response.data;
    } catch (error) {
      console.error('Error deleting jury:', error);
      throw error;
    }
  };

  return { approveJury, rejectJury, deleteJuryRegistration, createJury, updateJury, deleteJury };
}

// Document

export function useGetDocumentById(id) {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    id ? zerreactorBackendEndpoints.Document.Get(id) : null,
    unAuthenticatedSwrOptions
  );

  // Process the response to build full URLs from ShareUrl
  const processedData = useMemo(() => {
    if (!data || !data.Response) return data;

    const serverAccess = data.Response.ServerAccess;
    if (serverAccess && serverAccess.ShareUrl) {
      // Build full URL from ShareUrl and server URL
      const fullUrl = `${CONFIG.site.serverUrl}${serverAccess.ShareUrl}`;

      return {
        ...data,
        Response: {
          ...data.Response,
          ServerAccess: {
            ...serverAccess,
            FullUrl: fullUrl, // Add full URL for convenience
          },
        },
      };
    }

    return data;
  }, [data]);

  const memoizedValue = useMemo(
    () => ({
      document: processedData || {},
      documentLoading: isLoading,
      documentError: error,
      documentValidating: isValidating,
    }),
    [processedData, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAllDocuments(documentTypeId = null) {
  // Build the URL with DocumentTypeId parameter - only if documentTypeId is provided
  const endpoint = documentTypeId
    ? `${zerreactorBackendEndpoints.Document.GetAll}?DocumentTypeId=${documentTypeId}`
    : null; // This will prevent SWR from making any API call when documentTypeId is null

  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    endpoint, // SWR will not fetch if this is null
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      documents: data || [],
      documentsLoading: isLoading,
      documentsError: error,
      documentsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useDocumentOperations() {
  const createDocument = async (formData) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Document.Create,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Log current cache keys for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Document Create - Before cache invalidation:', {
          response: response.data,
          cacheKeys:
            typeof window !== 'undefined' && window.__SWR_CACHE__
              ? Object.keys(window.__SWR_CACHE__).filter((key) => key.includes('Document'))
              : 'Cache not accessible',
        });
      }

      // Comprehensive cache invalidation strategy with forced revalidation
      // Wait for all cache invalidations to complete and trigger refetch
      await Promise.all([
        // 1. Base endpoint with revalidation
        mutate(zerreactorBackendEndpoints.Document.GetAll, undefined, { revalidate: true }),

        // 2. Explicit common DocumentTypeId endpoints with revalidation
        mutate(`${zerreactorBackendEndpoints.Document.GetAll}?DocumentTypeId=1`, undefined, {
          revalidate: true,
        }),
        mutate(`${zerreactorBackendEndpoints.Document.GetAll}?DocumentTypeId=6`, undefined, {
          revalidate: true,
        }),
      ]);

      // 3. Pattern-based invalidation for any other DocumentTypeId endpoints
      await mutate(
        (key) => typeof key === 'string' && key.includes('/api/Document/GetAll'),
        undefined,
        { revalidate: true }
      );

      // 4. Log after invalidation for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('Document Create - Cache invalidation completed with revalidation');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  };

  const getDocumentById = async (id) => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Document.Get(id));

      // Process the response to build full URLs from ShareUrl
      if (response.data && response.data.Response && response.data.Response.ServerAccess) {
        const serverAccess = response.data.Response.ServerAccess;
        if (serverAccess.ShareUrl) {
          // Build full URL from ShareUrl and server URL
          const fullUrl = `${CONFIG.site.serverUrl}${serverAccess.ShareUrl}`;

          response.data.Response.ServerAccess.FullUrl = fullUrl;
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  };

  const getMultipleDocumentsByIds = async (ids) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Document.GetMultipleByIds,
        { IdList: ids }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting multiple documents:', error);
      throw error;
    }
  };

  const deleteDocument = async (id) => {
    try {
      const response = await axiosInstance.delete(zerreactorBackendEndpoints.Document.Delete(id));

      // Log current cache keys for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Document Delete - Before cache invalidation:', {
          deletedId: id,
          cacheKeys:
            typeof window !== 'undefined' && window.__SWR_CACHE__
              ? Object.keys(window.__SWR_CACHE__).filter((key) => key.includes('Document'))
              : 'Cache not accessible',
        });
      }

      // Comprehensive cache invalidation strategy with forced revalidation
      // Wait for all cache invalidations to complete and trigger refetch
      await Promise.all([
        // 1. Base endpoint with revalidation
        mutate(zerreactorBackendEndpoints.Document.GetAll, undefined, { revalidate: true }),

        // 2. Explicit common DocumentTypeId endpoints with revalidation
        mutate(`${zerreactorBackendEndpoints.Document.GetAll}?DocumentTypeId=1`, undefined, {
          revalidate: true,
        }),
        mutate(`${zerreactorBackendEndpoints.Document.GetAll}?DocumentTypeId=6`, undefined, {
          revalidate: true,
        }),
      ]);

      // 3. Pattern-based invalidation for any other DocumentTypeId endpoints
      await mutate(
        (key) => typeof key === 'string' && key.includes('/api/Document/GetAll'),
        undefined,
        { revalidate: true }
      );

      // 4. Log after invalidation for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('Document Delete - Cache invalidation completed with revalidation');
      }

      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  };

  return { createDocument, getDocumentById, deleteDocument, getMultipleDocumentsByIds };
}

// Judgement

export function useGetAllTempJudgementPeriods() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Temp.GetJudgementPeriods,
    unAuthenticatedSwrOptions
  );

  console.log('data', data);

  return useMemo(
    () => ({
      judgementPeriods: data || [],
      judgementPeriodsLoading: isLoading,
      judgementPeriodsError: error,
      judgementPeriodsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAllTempJudgementSessions() {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    zerreactorBackendEndpoints.Temp.GetJudgementSessions,
    unAuthenticatedSwrOptions
  );

  return useMemo(
    () => ({
      judgementSessions: data || [],
      judgementSessionsLoading: isLoading,
      judgementSessionsError: error,
      judgementSessionsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

// Report

export function useGetReportOperations() {
  const getAffiliates = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Affiliates);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Affiliates, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error gettig Affiliates:', error);
      throw error;
    }
  }, []);

  const getCurrentFunds = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.CurrentFunds);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.CurrentFunds, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error gettig Current Funds:', error);
      throw error;
    }
  }, []);

  const getProjects = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Projects);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Projects, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error getting Projects:', error);
      throw error;
    }
  }, []);

  const getJudges = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Judges);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Judges, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error gettig Judges:', error);
      throw error;
    }
  }, []);

  const getStudents = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Students);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Students, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error gettig Students:', error);
      throw error;
    }
  }, []);

  const getParents = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Parents);
      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Parents, false);
      }
      return response.data;
    } catch (error) {
      console.error('Error getting getParents:', error);
      throw error;
    }
  }, []);

  const getGuests = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Guests);
      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Guests, false);
      }
      return response.data;
    } catch (error) {
      console.error('Error getting Guests:', error);
      throw error;
    }
  }, []);
  
  const getObservers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Observers);
      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Observers, false);
      }
      return response.data;
    } catch (error) {
      console.error('Error getting Observers:', error);
      throw error;
    }
  }, []);
  
  const getRelatives = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Relatives);
      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Relatives, false);
      }
      return response.data;
    } catch (error) {
      console.error('Error getting Relatives:', error);
      throw error;
    }
  }, []);

  const getSupervisors = useCallback(async () => {
    try {
      const response = await axiosInstance.get(zerreactorBackendEndpoints.Report.Supervisors);

      if (response.data) {
        mutate(zerreactorBackendEndpoints.Report.Supervisors, false);
      }

      return response.data;
    } catch (error) {
      console.error('Error gettig Supervisors:', error);
      throw error;
    }
  }, []);

  return useMemo(
    () => ({
      getAffiliates,
      getCurrentFunds,
      getProjects,
      getJudges,
      getStudents,
      getSupervisors,
      getParents,
      getGuests,
      getObservers,
      getRelatives,
    }),
    [
      getAffiliates,
      getCurrentFunds,
      getProjects,
      getJudges,
      getStudents,
      getSupervisors,
      getParents,
      getGuests,
      getObservers,
      getRelatives,]
  );
}

// Mailing
export function useMailingOperations() {
  const sendEmail = async (emailData) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Mailing.SendEmail,
        emailData
      );
      return response.data;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  return { sendEmail };
}

// Competition
export function useGetActiveCompetitions(enabled = true) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    enabled ? zerreactorBackendEndpoints.Competition.GetActiveCompetitions : null,
    authenticatedSwrOptions
  );

  // console.log('useGetActiveCompetitions hook result:', {
  //     enabled,
  //     endpoint: enabled ? zerreactorBackendEndpoints.Competition.GetActiveCompetitions : 'disabled',
  //     data,
  //     isLoading,
  //     error,
  //     isValidating
  // });

  const memoizedValue = useMemo(
    () => ({
      activeCompetitions: data || [],
      activeCompetitionsLoading: isLoading,
      activeCompetitionsError: error,
      activeCompetitionsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAllCompetitions() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Competition.GetAll,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      competitions: data || [],
      competitionsLoading: isLoading,
      competitionsError: error,
      competitionsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAllCompetitionsAdmin() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Competition.GetAllAdmin,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      competitionsAdmin: data || [],
      competitionsAdminLoading: isLoading,
      competitionsAdminError: error,
      competitionsAdminValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Auth operations
export function useUpdateSelectedCompetitionId() {
  const updateSelectedCompetitionId = async (competitionId) => {
    try {
      const response = await axiosInstance.put(
        zerreactorBackendEndpoints.Auth.updateSelectedCompetitionId(competitionId)
      );

      // Reload the page after successful competition update
      if (response.status === 200) {
        mutate(zerreactorBackendEndpoints.Auth.profile);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }

      return response.data;
    } catch (error) {
      console.error('Error updating selected competition ID:', error);
      throw error;
    }
  };

  return { updateSelectedCompetitionId };
}

// Certificate
export function useGetCertificateById(id) {
  const { data, isLoading, error, isValidating } = useUnauthenticatedSWR(
    id ? zerreactorBackendEndpoints.ParticipantCertificates.GetCertificateById(id) : null,
    unAuthenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      certificate: data || null,
      certificateLoading: isLoading,
      certificateError: error,
      certificateValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useInitiatePassword() {
  const initiatePassword = async (password, token) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Auth.initiatePassword,
        { Password: password },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // If response.data is null but request.responseText exists, parse it
      if (response.data === null && response.request?.responseText) {
        const parsedResponse = JSON.parse(response.request.responseText);
        return parsedResponse;
      }

      return response.data;
    } catch (error) {
      console.error('Error initiating password:', error);
      throw error;
    }
  };

  return { initiatePassword };
}

export function useSendVerificationEmail() {
  const sendVerificationEmail = async (email) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Auth.sendVerificationEmail,
        { Email: email }
      );

      // If response.data is null but request.responseText exists, parse it
      if (response.data === null && response.request?.responseText) {
        const parsedResponse = JSON.parse(response.request.responseText);
        return parsedResponse;
      }

      return response.data;
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  };

  return { sendVerificationEmail };
}

// Home Guideline
export function useGetHomeGuidelineInformations() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Home.GuidelineInformations,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      guidelineInfo: data || {},
      guidelineInfoLoading: isLoading,
      guidelineInfoError: error,
      guidelineInfoValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Feedback
export function useGetAllFeedbacks() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Feedback.GetAll,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      feedbacks: data || [],
      feedbacksLoading: isLoading,
      feedbacksError: error,
      feedbacksValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useFeedbackOperations() {
  const submitFeedback = async (feedbackData) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Feedback.Submit,
        feedbackData
      );
      mutate(zerreactorBackendEndpoints.Auth.feedbacks);
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  const replyFeedback = async (id, feedbackData) => {
    try {
      const response = await axiosInstance.post(
        zerreactorBackendEndpoints.Feedback.Reply(id),
        feedbackData
      );
      return response.data;
    } catch (error) {
      console.error('Error replying feedback:', error);
      throw error;
    }
  };

  const closeFeedback = async (id) => {
    try {
      const response = await axiosInstance.post(zerreactorBackendEndpoints.Feedback.Close(id));
      return response.data;
    } catch (error) {
      console.error('Error closing feedback:', error);
      throw error;
    }
  };

  const deleteFeedback = async (id) => {
    try {
      const response = await axiosInstance.delete(zerreactorBackendEndpoints.Feedback.Delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting feedback:', error);
      throw error;
    }
  };

  return { submitFeedback, replyFeedback, closeFeedback, deleteFeedback };
}
